import React, { Component } from "react";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";
import { Sidebar, SidebarItem } from "../../CommonUI/BuilderUI/Sidebar";
import { DataIcon } from "../../CommonUI/Icon/DataIcon";
import { RuleTableIcon } from "../../CommonUI/Icon/RuleTableIcon";

import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { RuleEditorDiv } from "../DesignUI/CommonStyles";
import { IntersectionRuleEditContext } from "../../rule-engine-core/Rule/Intersection/IntersectionRuleEditContext";
import { IntersectionRuleConfiguration } from "../../rule-engine-core/Rule/Intersection/IntersectionRuleConfiguration";
import { IntersectionRuleDesigner } from "./Builder/IntersectionRuleDesigner";
import { Topbar } from "../../CommonUI/BuilderUI/Topbar";

import { XExecutableTester } from "../../CommonUI/Testing/XExecutableTester";
import { TestDataIcon } from "../../CommonUI/Icon/TestDataIcon";
import { BuilderEditPage } from "../../CommonUI/BuilderUI/BuilderEditPage";
import { CodingIcon } from "../../CommonUI/Icon/CodingIcon";
import { DataBuilder } from "../../DataBuilder/DataBuilder";
import { RuleBuilderPage } from "../../rule-engine-core/Rule/RuleBuilderPage";
import { UnitTestConfiguration } from "../../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";

export interface IIntersectionRuleEditor {
    ruleDefinition: IntersectionRuleConfiguration;
    intersectionRuleName?: string;
    onIntersectionRuleNameUpdate?: (newName: string) => void;
    onRuleSave?: () => void;
    onRuleCancel?: () => void;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    getUnitTests?: () => Promise<UnitTestConfiguration[]>;
}

// extends base editor concept, can be functional or regular components
export class IntersectionRuleBuilder extends Component<IIntersectionRuleEditor, any> {
    editContext: IntersectionRuleEditContext;
    tabs: SidebarItem[];
    ruleDefinition: IntersectionRuleConfiguration;

    constructor(props: IIntersectionRuleEditor) {
        super(props);

        this.tabs = this.getDefaultTabs();
        this.ruleDefinition = props.ruleDefinition;

        this.editContext = new IntersectionRuleEditContext(this.ruleDefinition);

        if (props.intersectionRuleName) {
            this.editContext.title = props.intersectionRuleName;
        }

        if (props.onRuleSave) {
            this.editContext.onRuleSave = props.onRuleSave;
        }

        if (props.onRuleCancel) {
            this.editContext.onRuleCancel = props.onRuleCancel;
        }
    }

    @AutoBind
    onChangeRuleName(newName: string) {
        this.editContext.title = newName;
        StateManager.propertyChanged(this.editContext, "title");
        if (this.props.onIntersectionRuleNameUpdate) this.props.onIntersectionRuleNameUpdate(newName);
    }

    getDefaultTabs(): SidebarItem[] {
        return [
            {
                icon: <RuleTableIcon />,
                value: RuleBuilderPage.Rule,
                renderUI: () => (
                    <IntersectionRuleDesigner
                        editContext={this.editContext}
                        ruleDefinition={this.ruleDefinition}
                    />
                ),
            },
            {
                value: RuleBuilderPage.Data,
                renderUI: () => (
                    <DataBuilder
                        definition={this.props.ruleDefinition.getInputDefinition()}
                        readOnly={!this.props.ruleDefinition.canChangeInputs}
                    />
                ),
                icon: <DataIcon />,
            },
            {
                value: RuleBuilderPage.Configuration,
                renderUI: () => (
                    <BuilderEditPage
                        builder={this.props.ruleDefinition}
                        updateCallback={() => {}}
                    />
                ),
                icon: <CodingIcon />,
            },
            {
                value: RuleBuilderPage.Test,
                renderUI: () => (
                    <XExecutableTester
                        saveUnitTest={this.props.saveUnitTest}
                        executable={this.props.ruleDefinition}
                    />
                ),
                icon: <TestDataIcon />,
            },
        ];
    }

    render() {
        return (
            <RuleEditorDiv>
                <ObserveState
                    listenTo={this.editContext}
                    properties={["title"]}
                    control={() => {
                        return (
                            <Topbar
                                onChangeName={this.onChangeRuleName}
                                editContext={this.editContext}
                                name={this.editContext.title}
                                isActionDisabled={!this.editContext.lastSelectionElement}
                                isRequiredActionGroup={false}
                                onSave={
                                    this.editContext.onRuleSave
                                        ? () => {
                                              if (this.editContext.onRuleSave) {
                                                  this.editContext.onRuleSave();
                                              }
                                          }
                                        : undefined
                                }
                                onClose={this.editContext.onRuleCancel}
                            />
                        );
                    }}
                />
                <ObserveState
                    listenTo={this.editContext}
                    properties={["mode"]}
                    control={() => (
                        <Sidebar
                            items={this.tabs}
                            value={this.editContext.mode}
                            onChange={(mode: string) => {
                                Reflect.set(this.editContext, "mode", mode);
                                StateManager.propertyChanged(this.editContext, "mode");
                            }}
                        />
                    )}
                />
            </RuleEditorDiv>
        );
    }
}
